import React from 'react';

import { AlertDialog, ContentDialog } from '@reckon-web/modal-dialog';
import { Text } from '@reckon-web/text';

import { useOpenJobDialog } from '../../hooks/use-open-job-dialog';

import { NewJob } from './new-job';
import { NewCloseArgs } from './new';

export type Opts =
  | { mode: 'create'; statusId?: string }
  | { mode: 'recur'; recurJobId: string };

const OpenNewJobDialogContext = React.createContext<(opts?: Opts) => void>(
  () => {}
);
OpenNewJobDialogContext.displayName = 'OpenNewJobDialogContext';

export const useOpenNewJobDialog = () =>
  React.useContext(OpenNewJobDialogContext);

export const NewJobDialogProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const openJob = useOpenJobDialog();
  const [isOpen, setIsOpen] = React.useState(false);
  const [opts, setOpts] = React.useState<Opts>();
  // for passing into the child component, so it can indicate if we should prompt on close or not
  const [shouldPromptUnsaved, setShouldPromptUnsaved] =
    React.useState<boolean>();
  // for our use, to show the unsaved prompt
  const [isPromptingUnsaved, setIsPromptingUnsaved] = React.useState<boolean>();

  const onClose = React.useCallback(
    (args: NewCloseArgs) => {
      // only prompt if they hit cancel. If they hit OK, and it worked, we're not dirty
      if (shouldPromptUnsaved && args.didCancel) {
        // pop the confirmation
        setIsPromptingUnsaved(true);
        // we're done here
        return;
      }
      if (!args.didCancel && args.jobId) {
        // they actually made a job. Good on them. Open the job
        openJob(args.jobId);
      }

      setIsOpen(false);
    },
    [openJob, shouldPromptUnsaved]
  );

  const tryCloseDialog = React.useCallback(() => {
    if (shouldPromptUnsaved) {
      setIsPromptingUnsaved(true);
    } else {
      setIsOpen(false);
    }
  }, [shouldPromptUnsaved]);

  return (
    <OpenNewJobDialogContext.Provider
      value={(opts = { mode: 'create' }) => {
        setOpts(opts);
        setIsOpen(true);
      }}
    >
      {children}
      {/* TODO this is begging to be turned into a reusable component that uses alex's openDialog. That way, it could be awaited in a click handler and the spaghetti of state could be relaxed */}
      <AlertDialog
        isOpen={Boolean(isPromptingUnsaved)}
        actions={{
          confirm: {
            label: 'Yes, discard',
            action: () => {
              // close both dialogs
              setIsPromptingUnsaved(undefined);
              setIsOpen(false);
            },
          },
          cancel: {
            label: `Continue creating`,
            action: () => setIsPromptingUnsaved(undefined),
          },
        }}
        title="Discard changes?"
        tone="critical"
      >
        <Text>Changes you've made so far will not be saved.</Text>
      </AlertDialog>
      <ContentDialog
        size="small"
        isOpen={isOpen}
        onClose={tryCloseDialog}
        css={{ width: '95vw', height: '95vh' }}
      >
        <NewJob
          onClose={onClose}
          opts={opts}
          setShouldPromptUnsaved={setShouldPromptUnsaved}
        />
      </ContentDialog>
    </OpenNewJobDialogContext.Provider>
  );
};
